import { useReducedMotion } from '@/lib/useReducedMotion';
import { useEffect, useRef } from 'react';
import { sampleMagneticField, type Magnet, type FieldSample } from './stormField';

interface StormBackgroundProps {
  /** Color of the brighter ~14% of motes: gold (Current concept) or ice (nVision concept). */
  accent?: 'gold' | 'ice';
}

interface Mote { x: number; y: number; life: number; w: number }
interface Bubble { x: number; y: number; r: number; v: number; ph: number }

const GROUND = '#060B1E';

/** Smooth scalar field; its curl is a divergence-free flow. */
const pot = (x: number, y: number, t: number) =>
  Math.sin(x * 0.0062 + t * 0.11) * Math.cos(y * 0.0074 - t * 0.09) * 22 +
  Math.sin((x + y) * 0.0031 - t * 0.16) * 15 +
  Math.cos(x * 0.0017 - y * 0.0021 + t * 0.06) * 26;

/**
 * Full-viewport "Current" sky: a bounded set of motes carried by a curl-noise field,
 * with slow rising bubbles. The pointer (mouse or finger) drops a vortex into the flow.
 */
export const StormBackground = ({ accent = 'gold' }: StormBackgroundProps) => {
  const reduced = useReducedMotion();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    const warmStroke = accent === 'gold' ? 'rgba(240,192,112,.34)' : 'rgba(143,227,240,.34)';
    let W = 0, H = 0, N = 0, mstr = 0, raf = 0;
    let lastPaint = 0, resumeTimer = 0, scrolling = false;
    let input: 'mouse' | 'touch' | null = null;
    let touchHeld = false, touchId: number | null = null, touchReleasedAt = 0;
    const touchLingerMs = 700;
    const magnet: Magnet = { x: -1e4, y: -1e4, strength: 0, reach: 220 };
    const field: FieldSample = { vx: 0, vy: 0, influence: 0 };
    let P: Mote[] = [];
    let bub: Bubble[] = [];

    const mk = (): Mote => ({ x: Math.random() * W, y: Math.random() * H, life: 40 + Math.random() * 150, w: Math.random() });
    const seed = () => {
      N = W < 1000 ? 180 : 360;
      P = Array.from({ length: N }, mk);
      bub = Array.from({ length: 9 }, () => ({
        x: Math.random() * W, y: Math.random() * H,
        r: 7 + Math.random() * 22, v: 0.10 + Math.random() * 0.22, ph: Math.random() * 6.28
      }));
    };
    const size = () => {
      const widthChanged = Math.round(W) !== window.innerWidth;
      W = Math.max(1, window.innerWidth);
      H = Math.max(1, window.innerHeight);
      magnet.reach = W < 640 ? 260 : 220;
      const d = Math.min(window.devicePixelRatio || 1, 1.5);
      cv.width = Math.round(W * d);
      cv.height = Math.round(H * d);
      ctx.setTransform(d, 0, 0, d, 0, 0);
      ctx.fillStyle = GROUND;
      ctx.fillRect(0, 0, W, H);
      // Mobile URL-bar show/hide only changes height — keep the motes instead of reseeding.
      if (widthChanged || !P.length) seed();
    };

    const touchWeight = (time: number) => touchHeld ? 1 : Math.max(0, 1 - (time - touchReleasedAt) / touchLingerMs);
    const touchActive = () => input === 'touch' && touchWeight(performance.now()) > 0;
    const frame = (ts: number) => {
      raf = 0;
      const weight = input === 'touch' ? touchWeight(ts) : input === 'mouse' ? 1 : 0;
      if (input === 'touch' && !weight) release();
      if (document.hidden || (scrolling && !weight)) return;
      if (!reduced && lastPaint && ts - lastPaint < 1000 / (W < 640 ? 24 : 30)) {
        raf = requestAnimationFrame(frame);
        return;
      }
      // Preserve speed at 30fps; never jump across the screen after a paused tab.
      const step = lastPaint ? Math.min((ts - lastPaint) / (1000 / 60), 2.5) : 1;
      lastPaint = ts;
      const t = ts / 1000;
      // The viewport field can follow a finger during scrolling without retaining old trails.
      ctx.fillStyle = scrolling ? GROUND : 'rgba(6,11,30,0.22)';
      ctx.fillRect(0, 0, W, H);

      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, 'rgba(6,11,30,0)');
      g.addColorStop(1, `rgba(30,52,120,${0.035 + 0.02 * Math.sin(t * 0.3)})`);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);

      mstr += (weight - mstr) * (1 - Math.pow(.82, step));
      magnet.strength = reduced ? 0 : mstr;

      ctx.globalCompositeOperation = 'lighter';
      const e = 1.6;
      for (let i = 0; i < N; i++) {
        const p = P[i];
        let vx = 0, vy = 0;
        if (!reduced) {
          const dpdy = (pot(p.x, p.y + e, t) - pot(p.x, p.y - e, t)) / (2 * e);
          const dpdx = (pot(p.x + e, p.y, t) - pot(p.x - e, p.y, t)) / (2 * e);
          vx = dpdy * 7.4;
          vy = -dpdx * 7.4;
          sampleMagneticField(p.x, p.y, magnet, field);
          vx += field.vx; vy += field.vy;
        }
        const px = p.x, py = p.y;
        p.x += vx * step; p.y += vy * step; p.life -= step;
        if (p.life < 0 || p.x < -8 || p.x > W + 8 || p.y < -8 || p.y > H + 8) { P[i] = mk(); continue; }
        const warm = p.w > 0.86;
        ctx.strokeStyle = warm ? warmStroke : `rgba(120,164,255,${(0.09 + p.w * 0.20 + field.influence * .28).toFixed(3)})`;
        ctx.lineWidth = (warm ? 1.15 : 0.75) + field.influence * .65;
        ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(p.x, p.y); ctx.stroke();
      }
      ctx.globalCompositeOperation = 'source-over';

      for (const q of bub) {
        if (!reduced) { q.y -= q.v * step; q.ph += 0.01 * step; }
        if (q.y + q.r < 0) { q.y = H + q.r; q.x = Math.random() * W; }
        const bx = q.x + Math.sin(q.ph) * 7;
        const rg = ctx.createRadialGradient(bx, q.y, 0, bx, q.y, q.r);
        rg.addColorStop(0, 'rgba(234,240,255,.055)');
        rg.addColorStop(0.75, 'rgba(92,140,255,.035)');
        rg.addColorStop(1, 'rgba(92,140,255,0)');
        ctx.fillStyle = rg;
        ctx.beginPath(); ctx.arc(bx, q.y, q.r, 0, 6.2832); ctx.fill();
        ctx.strokeStyle = 'rgba(234,240,255,.11)';
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(bx, q.y, q.r, -1.0, 1.3); ctx.stroke();
      }
      if (!reduced && !document.hidden) {
        raf = requestAnimationFrame(frame);
      } else {
        raf = 0;
      }
    };

    const wake = () => {
      if (!reduced && !document.hidden && !raf && (!scrolling || touchActive())) raf = requestAnimationFrame(frame);
    };
    const point = (x: number, y: number, kind: 'mouse' | 'touch') => {
      magnet.x = x; magnet.y = y; input = kind;
      if (kind === 'touch') { touchHeld = true; mstr = Math.max(mstr, .65); }
      const roundedX = String(Math.round(x)), roundedY = String(Math.round(y));
      if (cv.dataset.magnet !== kind) cv.dataset.magnet = kind;
      if (cv.dataset.magnetX !== roundedX) cv.dataset.magnetX = roundedX;
      if (cv.dataset.magnetY !== roundedY) cv.dataset.magnetY = roundedY;
      wake();
    };
    const onPointer = (e: PointerEvent) => {
      if (e.pointerType === 'touch') { point(e.clientX, e.clientY, 'touch'); return; }
      if (!touchHeld) point(e.clientX, e.clientY, 'mouse');
    };
    const onTouch = (e: TouchEvent) => {
      const tc = touchId === null ? e.touches[0] : Array.from(e.touches).find(t => t.identifier === touchId);
      if (tc) { touchId = tc.identifier; point(tc.clientX, tc.clientY, 'touch'); }
    };
    const release = () => {
      magnet.x = -1e4; magnet.y = -1e4; input = null; touchHeld = false; touchId = null;
      cv.dataset.magnet = 'none';
      delete cv.dataset.magnetX; delete cv.dataset.magnetY;
    };
    const endTouch = () => {
      if (input !== 'touch') return;
      touchHeld = false; touchId = null; touchReleasedAt = performance.now();
    };
    const onPointerEnd = (e: PointerEvent) => {
      // Touch events retain contact during native pan after pointercancel.
      if (e.pointerType === 'touch' && touchId === null) endTouch();
    };
    const onMouseOut = (e: MouseEvent) => {
      if (matchMedia('(pointer: coarse)').matches) return;
      if (!e.relatedTarget) release();
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (touchId !== null && Array.from(e.touches).some(t => t.identifier === touchId)) return;
      endTouch();
    };
    const onTouchCancel = () => {
      release();
      if (scrolling && raf) { cancelAnimationFrame(raf); raf = 0; }
    };

    const onScroll = () => {
      if (reduced) return;
      const firstScroll = !scrolling;
      scrolling = true;
      if (!touchActive()) {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        lastPaint = 0;
        release();
      }
      // Remove accumulated trails immediately while the page is moving.
      if (firstScroll) {
        ctx.fillStyle = GROUND;
        ctx.fillRect(0, 0, W, H);
      }
      if (touchActive()) wake();
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => {
        scrolling = false;
        if (!document.hidden && !reduced && !raf) raf = requestAnimationFrame(frame);
      }, 150);
    };

    const onVisibilityChange = () => {
      lastPaint = 0;
      if (document.hidden) {
        release();
        if (raf) {
          cancelAnimationFrame(raf);
          raf = 0;
        }
      } else {
        if (!raf && !reduced && !scrolling) {
          raf = requestAnimationFrame(frame);
        }
      }
    };

    const hear = { passive: true, capture: true } as const;
    window.addEventListener('pointermove', onPointer, hear);
    window.addEventListener('pointerdown', onPointer, hear);
    window.addEventListener('pointerup', onPointerEnd, hear);
    window.addEventListener('pointercancel', onPointerEnd, hear);
    window.addEventListener('touchstart', onTouch, hear);
    window.addEventListener('touchmove', onTouch, hear);
    window.addEventListener('touchend', onTouchEnd, hear);
    window.addEventListener('touchcancel', onTouchCancel, hear);
    document.addEventListener('mouseout', onMouseOut);
    window.addEventListener('resize', size, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
    size();
    if (!document.hidden) {
      raf = requestAnimationFrame(frame);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.clearTimeout(resumeTimer);
      release();
      window.removeEventListener('pointermove', onPointer, hear);
      window.removeEventListener('pointerdown', onPointer, hear);
      window.removeEventListener('pointerup', onPointerEnd, hear);
      window.removeEventListener('pointercancel', onPointerEnd, hear);
      window.removeEventListener('touchstart', onTouch, hear);
      window.removeEventListener('touchmove', onTouch, hear);
      window.removeEventListener('touchend', onTouchEnd, hear);
      window.removeEventListener('touchcancel', onTouchCancel, hear);
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('resize', size);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [accent, reduced]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-rd-storm-effects
      className="fixed inset-0 w-screen h-screen pointer-events-none"
      style={{ zIndex: 0, background: GROUND }}
    />
  );
};
